import { motion } from "framer-motion";

export function Container({ children, className = "" }) {
  return (
    <div className={`mx-auto w-full max-w-7xl px-5 sm:px-8 lg:px-10 ${className}`}>
      {children}
    </div>
  );
}

export function Reveal({ children, delay = 0, className = "", y = 24 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export function Stagger({ children, className = "", gap = 0.08 }) {
  return (
    <motion.div
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-80px" }}
      transition={{ staggerChildren: gap }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export const staggerItem = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
  },
};

export function StaggerItem({ children, className = "", ...rest }) {
  return (
    <motion.div variants={staggerItem} className={className} {...rest}>
      {children}
    </motion.div>
  );
}

export function Eyebrow({ children, tone = "orange" }) {
  const tones = {
    orange: "bg-[var(--color-bg-light-orange)] text-primary",
    navy: "bg-white/10 text-white",
    white: "bg-white/90 text-navy",
  };
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-bold tracking-wide uppercase ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

export function EyebrowLine({ children, tone = "orange" }) {
  const tones = {
    orange: "text-primary-dark",
    navy: "text-navy",
    white: "text-white",
  };
  const bars = {
    orange: "from-primary",
    navy: "from-navy",
    white: "from-white",
  };
  return (
    <div className={`flex items-center gap-2.5 text-[11px] font-extrabold tracking-[0.22em] uppercase ${tones[tone]}`}>
      <span>{children}</span>
      <span className={`h-0.5 flex-1 rounded-full bg-gradient-to-r ${bars[tone]} to-transparent`} />
    </div>
  );
}

export function Button({
  as = "button",
  href,
  onClick,
  variant = "primary",
  size = "md",
  className = "",
  children,
  type = "button",
  ...rest
}) {
  const base =
    "focus-ring inline-flex items-center justify-center gap-2 rounded-2xl font-bold transition-all duration-200 active:scale-[0.98]";
  const sizes = {
    md: "px-6 py-3.5 text-sm sm:text-base",
    lg: "px-8 py-4 text-base sm:text-lg",
    sm: "px-4 py-2.5 text-sm",
  };
  const variants = {
    primary:
      "bg-gradient-to-br from-primary to-primary-dark text-white shadow-[0_12px_30px_-10px_rgba(255,90,31,0.6)] hover:scale-[1.02] hover:shadow-[0_16px_36px_-10px_rgba(255,90,31,0.75)]",
    navy: "bg-navy text-white hover:scale-[1.02] hover:bg-navy-light",
    outline:
      "border-2 border-navy/15 text-navy bg-white hover:border-primary hover:text-primary hover:scale-[1.02]",
    "outline-white":
      "border-2 border-white/40 text-white bg-white/5 hover:bg-white/10 hover:scale-[1.02]",
    ghost: "text-navy hover:bg-navy/5",
  };

  const classes = `${base} ${sizes[size]} ${variants[variant]} ${className}`;

  if (as === "a" || href) {
    return (
      <a href={href} className={classes} onClick={onClick} {...rest}>
        {children}
      </a>
    );
  }

  return (
    <button type={type} className={classes} onClick={onClick} {...rest}>
      {children}
    </button>
  );
}

export function Field({ label, error, children, id }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-bold text-navy">
        {label}
      </label>
      {children}
      {error && <p className="text-xs font-semibold text-red-600">{error}</p>}
    </div>
  );
}

export function TextInput({ id, error, className = "", ...rest }) {
  return (
    <input
      id={id}
      className={`focus-ring w-full rounded-xl border-2 bg-white px-4 py-3 text-sm font-medium text-navy placeholder:text-navy/30 transition-colors ${
        error ? "border-red-400" : "border-navy/12 focus:border-primary"
      } ${className}`}
      {...rest}
    />
  );
}

export function PillSelect({ options, value, onChange, name }) {
  return (
    <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={name}>
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          role="radio"
          aria-checked={value === opt}
          onClick={() => onChange(opt)}
          className={`focus-ring rounded-xl border-2 px-4 py-2.5 text-sm font-bold transition-all ${
            value === opt
              ? "border-primary bg-primary text-white"
              : "border-navy/12 bg-white text-navy/60 hover:border-primary/40"
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

export function SectionHeading({
  eyebrow,
  eyebrowTone,
  title,
  accent,
  body,
  align = "center",
  tone = "light",
}) {
  const alignment = align === "center" ? "items-center text-center mx-auto" : "items-start text-left";
  const bodyColor = tone === "dark" ? "text-white/70" : "text-navy/60";
  return (
    <div className={`flex max-w-2xl flex-col gap-4 ${alignment}`}>
      {eyebrow && <Eyebrow tone={eyebrowTone}>{eyebrow}</Eyebrow>}
      <h2
        className={`text-3xl leading-[1.05] font-black tracking-tight sm:text-4xl lg:text-5xl ${
          tone === "dark" ? "text-white" : "text-navy"
        }`}
      >
        {title} {accent && <span className="text-primary">{accent}</span>}
      </h2>
      {body && <p className={`text-base sm:text-lg ${bodyColor}`}>{body}</p>}
    </div>
  );
}
